import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import {
  readLocalDataRecoveryReport,
  type LocalDataRecoveryReport,
  type SanitizedOrderRow,
} from '../../services/recovery/localDataRecovery';
import { setLocalDataRecoveryOpen } from '../../services/sync/syncEngine';

type Props = { onClose: () => void };

function OrderFields({ order }: { order: SanitizedOrderRow }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
      <div>
        <dt className="font-bold text-slate-500">Order Number</dt>
        <dd className="break-all">{order.orderNumber ?? '—'}</dd>
      </div>
      <div>
        <dt className="font-bold text-slate-500">Claim Code</dt>
        <dd>{order.claimCode ?? '—'}</dd>
      </div>
      <div>
        <dt className="font-bold text-slate-500">Order ID</dt>
        <dd className="break-all">{order.orderId ?? '—'}</dd>
      </div>
      <div>
        <dt className="font-bold text-slate-500">Order status</dt>
        <dd>{order.orderStatus ?? '—'}</dd>
      </div>
      <div>
        <dt className="font-bold text-slate-500">Payment status</dt>
        <dd>{order.paymentStatus ?? '—'}</dd>
      </div>
      <div>
        <dt className="font-bold text-slate-500">Order creation time</dt>
        <dd>{order.createdAt ?? '—'}</dd>
      </div>
    </dl>
  );
}

function unavailableMessage(report: LocalDataRecoveryReport) {
  switch (report.unavailableReason) {
    case 'DATABASE_NOT_FOUND':
      return 'BabsPOSDB was not found in this app context. No database was opened or created.';
    case 'SCHEMA_VERSION_MISMATCH':
      return 'The local database version differs from this app. It was left unopened to avoid an upgrade.';
    case 'SAFE_READ_UNAVAILABLE':
      return 'A safe read-only inspection is unavailable in this browser context. No records were changed.';
    default:
      return '';
  }
}

export function LocalDataRecoveryDialog({ onClose }: Props) {
  const [report, setReport] = useState<LocalDataRecoveryReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [copyState, setCopyState] = useState('');

  useEffect(() => {
    setLocalDataRecoveryOpen(true);
    void readLocalDataRecoveryReport()
      .then(setReport)
      .finally(() => setLoading(false));
    return () => setLocalDataRecoveryOpen(false);
  }, []);

  const copyReport = async () => {
    if (!report) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(report, null, 2));
      setCopyState('Sanitized report copied.');
    } catch {
      setCopyState(
        'Clipboard unavailable. Keep this panel open and copy the visible report manually.',
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center bg-slate-950/60 p-4"
      role="presentation"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="local-data-recovery-title"
        className="flex max-h-[92dvh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-widest text-[#00549C]">
              Read-only · on this device
            </p>
            <h2 id="local-data-recovery-title" className="text-xl font-black text-[#003B70]">
              Local Data Recovery
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close local data recovery"
            className="grid h-11 w-11 place-items-center rounded-xl bg-slate-100 text-slate-700"
          >
            <X />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-auto p-5">
          {loading ? (
            <p role="status" className="py-8 text-center font-semibold text-slate-600">
              Reading local records…
            </p>
          ) : report ? (
            <div className="space-y-5" data-testid="local-data-recovery-report">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Summary label="Database version" value={report.databaseVersion ?? '—'} />
                <Summary label="Orders" value={report.counts.orders} />
                <Summary label="Payments" value={report.counts.payments} />
                <Summary label="Outbox" value={report.counts.outbox} />
              </div>
              {report.unavailableReason ? (
                <p
                  role="status"
                  className="rounded-xl bg-amber-50 p-4 text-sm font-semibold text-amber-900"
                >
                  {unavailableMessage(report)}
                </p>
              ) : (
                <>
                  <section aria-label="Saved orders" className="space-y-3">
                    <h3 className="font-black text-[#003B70]">Orders</h3>
                    {report.orders.length ? (
                      report.orders.map((order, index) => (
                        <article
                          key={`${order.orderId ?? 'order'}-${index}`}
                          className="rounded-xl border border-slate-200 p-4"
                        >
                          <OrderFields order={order} />
                        </article>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500">No order rows.</p>
                    )}
                  </section>

                  <section aria-label="Saved payments" className="space-y-3">
                    <h3 className="font-black text-[#003B70]">Payments</h3>
                    {report.payments.length ? (
                      report.payments.map((payment, index) => (
                        <p
                          key={`${payment.relatedOrderId ?? 'payment'}-${index}`}
                          className="rounded-xl border border-slate-200 p-4 text-sm"
                        >
                          <span className="font-bold text-slate-500">Related order ID</span>
                          <br />
                          <span className="break-all">{payment.relatedOrderId ?? '—'}</span>
                        </p>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500">No payment rows.</p>
                    )}
                  </section>

                  <section aria-label="Outbox events" className="space-y-3">
                    <h3 className="font-black text-[#003B70]">Outbox events</h3>
                    {report.outbox.length ? (
                      report.outbox.map((event, index) => (
                        <article
                          key={`${event.eventId ?? 'event'}-${index}`}
                          className="space-y-3 rounded-xl border border-slate-200 p-4"
                        >
                          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                            <div>
                              <dt className="font-bold text-slate-500">Outbox event ID</dt>
                              <dd className="break-all">{event.eventId ?? '—'}</dd>
                            </div>
                            <div>
                              <dt className="font-bold text-slate-500">Aggregate ID</dt>
                              <dd className="break-all">{event.aggregateId ?? '—'}</dd>
                            </div>
                            <div>
                              <dt className="font-bold text-slate-500">Event type</dt>
                              <dd>{event.eventType ?? '—'}</dd>
                            </div>
                            <div>
                              <dt className="font-bold text-slate-500">Outbox status</dt>
                              <dd>{event.status ?? '—'}</dd>
                            </div>
                            <div>
                              <dt className="font-bold text-slate-500">Attempts / retry count</dt>
                              <dd>{event.attempts ?? '—'}</dd>
                            </div>
                          </dl>
                          {event.payloadOrder && (
                            <div className="border-t border-slate-100 pt-3">
                              <p className="mb-2 text-xs font-extrabold uppercase tracking-wide text-slate-500">
                                Sanitized order fields from event
                              </p>
                              <OrderFields order={event.payloadOrder} />
                            </div>
                          )}
                        </article>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500">No outbox rows.</p>
                    )}
                  </section>
                </>
              )}
            </div>
          ) : null}
        </div>

        <footer className="flex flex-col gap-3 border-t border-slate-200 p-4 sm:flex-row sm:justify-end">
          {copyState && (
            <p role="status" className="mr-auto self-center text-sm font-semibold text-slate-600">
              {copyState}
            </p>
          )}
          <button
            type="button"
            onClick={() => void copyReport()}
            disabled={!report || Boolean(report.unavailableReason)}
            className="min-h-12 rounded-xl bg-[#00549C] px-5 font-black text-white disabled:opacity-50"
          >
            COPY SANITIZED REPORT
          </button>
          <button
            type="button"
            onClick={onClose}
            className="min-h-12 rounded-xl border border-slate-300 px-5 font-black text-slate-700"
          >
            CLOSE
          </button>
        </footer>
      </section>
    </div>
  );
}

function Summary({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl bg-slate-50 p-3">
      <p className="text-xs font-bold text-slate-500">{label}</p>
      <p className="mt-1 text-lg font-black text-[#003B70]">{value}</p>
    </div>
  );
}
